import mixpanel from 'mixpanel-browser';
import { MIXPANEL_TOKEN, MIXPANEL_DEBUG, MIXPANEL_CONFIG_OPTIONS } from '../configs/mixpanel';
import { PLATFORM_EVENTS, FUNNELS } from '../configs/analyticsEvents';

class AnalyticsService {
  constructor() {
    this.isInitialized = false;
    this.sessionStartTime = null;
    this.funnelSteps = new Map();
    this.userProperties = new Map();
  }

  // Initialize Mixpanel
  init() {
    if (this.isInitialized) return;

    try {
      mixpanel.init(MIXPANEL_TOKEN, MIXPANEL_CONFIG_OPTIONS);
      this.isInitialized = true;
      
      if (MIXPANEL_DEBUG) {
        console.log('Mixpanel initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error);
    }
  }

  // Identify user
  identify(userId, userProperties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      mixpanel.identify(userId);
      
      if (Object.keys(userProperties).length > 0) {
        this.setUserProperty(userProperties);
      }
      
      if (MIXPANEL_DEBUG) {
        console.log('User identified:', userId, userProperties);
      }
    } catch (error) {
      console.error('Failed to identify user:', error);
    }
  }

  // Track custom event
  track(eventName, properties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      const eventData = {
        ...properties,
        timestamp: new Date().toISOString(),
        session_id: this.sessionStartTime,
        ...this.getCommonProperties()
      };

      mixpanel.track(eventName, eventData);
      
      if (MIXPANEL_DEBUG) {
        console.log('Event tracked:', eventName, eventData);
      }
    } catch (error) {
      console.error('Failed to track event:', error);
    }
  }

  // Track page view
  trackPageView(pageName, properties = {}) {
    // Map page names to proper event keys
    const pageEventMap = {
      'Home': 'VIEW_HOME_SCREEN',
      'QARA': 'VIEW_QARA_SCREEN', 
      'Cry Analyser': 'VIEW_CRY_ANALYSER_SCREEN',
      'Experts': 'VIEW_EXPERTS_SCREEN',
      'For You': 'VIEW_FOR_YOU_SCREEN',
      'Assessment': 'VIEW_ASSESSMENT_SCREEN',
      'Profile': 'VIEW_PROFILE_SCREEN',
      'Login': 'VIEW_AUTH_SCREEN',
      'Welcome': 'VIEW_WELCOME_SCREEN',
      'Enter Name': 'VIEW_NAME_INPUT_SCREEN',
      'Verify OTP': 'VIEW_OTP_SCREEN',
      'Privacy Policy': 'VIEW_PRIVACY_POLICY_SCREEN',
      'Terms of Service': 'VIEW_TERMS_OF_SERVICE_SCREEN',
      'Referral': 'VIEW_REFERRAL_SCREEN',
      'Review Login': 'VIEW_REVIEW_LOGIN_SCREEN'
    };

    // Get the proper event key for this page
    const eventKey = pageEventMap[pageName] || 'VIEW_GENERIC_SCREEN';
    
    // Use the proper event name from PLATFORM_EVENTS if available
    if (PLATFORM_EVENTS[eventKey]) {
      this.track(PLATFORM_EVENTS[eventKey].name, {
        page_name: pageName,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        ...properties
      });
    } else {
      // Fallback to generic page view if no specific event is defined
      this.track('page_view', {
        page_name: pageName,
        page_url: typeof window !== 'undefined' ? window.location.href : '',
        page_title: typeof document !== 'undefined' ? document.title : '',
        page_path: typeof window !== 'undefined' ? window.location.pathname : '',
        ...properties
      });
    }
  }

  // Set user property
  setUserProperty(properties) {
    if (!this.isInitialized) this.init();
    
    try {
      // Map common app-specific keys to Mixpanel reserved people properties
      const mappedProperties = {
        ...(properties && typeof properties === 'object' ? properties : {}),
      };

      if (mappedProperties.user_name && !mappedProperties.$name) {
        mappedProperties.$name = mappedProperties.user_name;
      }
      if (mappedProperties.name && !mappedProperties.$name) {
        mappedProperties.$name = mappedProperties.name;
      }
      if (mappedProperties.email && !mappedProperties.$email) {
        mappedProperties.$email = mappedProperties.email;
      }
      if (mappedProperties.phone && !mappedProperties.$phone) {
        mappedProperties.$phone = mappedProperties.phone;
      }

      Object.entries(mappedProperties).forEach(([key, value]) => {
        this.userProperties.set(key, value);
        mixpanel.people.set(key, value);
      });
      
      if (MIXPANEL_DEBUG) {
        console.log('User properties set:', mappedProperties);
      }
    } catch (error) {
      console.error('Failed to set user properties:', error);
    }
  }

  // Increment user property
  incrementUserProperty(property, value = 1) {
    if (!this.isInitialized) this.init();
    
    try {
      const currentValue = this.userProperties.get(property) || 0;
      const newValue = currentValue + value;
      
      this.userProperties.set(property, newValue);
      mixpanel.people.increment(property, value);
      
      if (MIXPANEL_DEBUG) {
        console.log('User property incremented:', property, 'by', value, 'to', newValue);
      }
    } catch (error) {
      console.error('Failed to increment user property:', error);
    }
  }

  // Track funnel step
  trackFunnelStep(funnelName, stepName, stepNumber, properties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      const funnelKey = `${funnelName}_${stepName}`;
      this.funnelSteps.set(funnelKey, {
        step: stepNumber,
        timestamp: new Date().toISOString(),
        ...properties
      });

      this.track('Funnel Step', {
        funnel_name: funnelName,
        step_name: stepName,
        step_number: stepNumber,
        total_steps: FUNNELS[funnelName]?.steps?.length || 0,
        ...properties
      });
      
      if (MIXPANEL_DEBUG) {
        console.log('Funnel step tracked:', funnelName, stepName, stepNumber);
      }
    } catch (error) {
      console.error('Failed to track funnel step:', error);
    }
  }

  // Track retention event
  trackRetentionEvent(eventName, properties = {}) {
    this.track('Retention Event', {
      event_name: eventName,
      days_since_signup: this.getDaysSinceSignup(),
      ...properties
    });
  }

  // Track churn event
  trackChurnEvent(reason, properties = {}) {
    this.track('Churn Event', {
      churn_reason: reason,
      days_since_signup: this.getDaysSinceSignup(),
      last_activity: this.getLastActivity(),
      ...properties
    });
  }

  // Track feature usage
  trackFeatureUsage(featureName, action, properties = {}) {
    this.track('Feature Usage', {
      feature_name: featureName,
      action: action,
      usage_count: this.getFeatureUsageCount(featureName),
      ...properties
    });
  }

  // Track error
  trackError(errorType, errorMessage, properties = {}) {
    this.track('Error', {
      error_type: errorType,
      error_message: errorMessage,
      page_url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      ...properties
    });
  }

  // Track performance
  trackPerformance(metricName, value, unit = 'ms', properties = {}) {
    this.track('Performance', {
      metric_name: metricName,
      value: value,
      unit: unit,
      ...properties
    });
  }

  // Track revenue
  trackRevenue(amount, currency = 'USD', properties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      mixpanel.track('Revenue', {
        amount: amount,
        currency: currency,
        ...properties
      });
      
      if (MIXPANEL_DEBUG) {
        console.log('Revenue tracked:', amount, currency);
      }
    } catch (error) {
      console.error('Failed to track revenue:', error);
    }
  }

  // Track experiment
  trackExperiment(experimentName, variant, properties = {}) {
    this.track('Experiment', {
      experiment_name: experimentName,
      variant: variant,
      ...properties
    });
  }

  // Start session
  startSession() {
    this.sessionStartTime = new Date().toISOString();
    this.track('Session Start', {
      session_id: this.sessionStartTime
    });
  }

  // End session
  endSession() {
    if (this.sessionStartTime) {
      const sessionDuration = Date.now() - new Date(this.sessionStartTime).getTime();
      
      this.track('Session End', {
        session_id: this.sessionStartTime,
        session_duration_ms: sessionDuration
      });
      
      this.sessionStartTime = null;
    }
  }

  // Reset analytics
  reset() {
    try {
      mixpanel.reset();
      this.funnelSteps.clear();
      this.userProperties.clear();
      this.sessionStartTime = null;
      
      if (MIXPANEL_DEBUG) {
        console.log('Analytics reset');
      }
    } catch (error) {
      console.error('Failed to reset analytics:', error);
    }
  }

  // Helper methods
  getCommonProperties() {
    return {
      platform: 'web',
      app_version: process.env.REACT_APP_VERSION || '1.0.0',
      environment: process.env.NODE_ENV || 'development'
    };
  }

  getDaysSinceSignup() {
    const signupDate = this.userProperties.get('signup_date');
    if (!signupDate) return null;
    
    const signup = new Date(signupDate);
    const now = new Date();
    const diffTime = Math.abs(now - signup);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  getLastActivity() {
    return this.userProperties.get('last_activity') || null;
  }

  getFeatureUsageCount(featureName) {
    const key = `feature_usage_${featureName}`;
    return this.userProperties.get(key) || 0;
  }

  // Platform event tracking methods
  trackPlatformEvent(eventKey, properties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      if (PLATFORM_EVENTS[eventKey]) {
        const event = PLATFORM_EVENTS[eventKey];
        const eventData = {
          ...properties,
          timestamp: new Date().toISOString(),
          session_id: this.sessionStartTime,
          event_category: event.category,
          event_feature: event.feature,
          event_importance: event.importance,
          event_owner: event.owner,
          ...this.getCommonProperties()
        };

        mixpanel.track(event.name, eventData);
        
        // Always log for debugging
        console.log('🎯 Platform event tracked:', event.name, eventData);
        
        if (MIXPANEL_DEBUG) {
          console.log('Mixpanel debug:', event.name, eventData);
        }
      } else {
        console.warn(`Event key '${eventKey}' not found in PLATFORM_EVENTS`);
        // Fallback to generic event tracking
        this.track(eventKey, properties);
      }
    } catch (error) {
      console.error('Failed to track platform event:', error);
    }
  }

  // Track screen view with proper event name
  trackScreenView(screenName, properties = {}) {
    if (!this.isInitialized) this.init();
    
    try {
      const screenEventMap = {
        'Home': 'VIEW_HOME_SCREEN',
        'QARA': 'VIEW_QARA_CHAT_SCREEN', 
        'Cry Analyser': 'VIEW_CRY_ANALYSER_SCREEN',
        'Experts': 'VIEW_EXPERTS_SCREEN',
        'For You': 'VIEW_FOR_YOU_SCREEN',
        'Assessment': 'VIEW_ASSESSMENT_SCREEN',
        'Profile': 'VIEW_PROFILE_SCREEN',
        'Login': 'VIEW_AUTH_SCREEN',
        'Welcome': 'VIEW_WELCOME_SCREEN',
        'Enter Name': 'VIEW_NAME_INPUT_SCREEN',
        'Verify OTP': 'VIEW_OTP_SCREEN',
        'Privacy Policy': 'VIEW_PRIVACY_POLICY_SCREEN',
        'Terms of Service': 'VIEW_TERMS_OF_SERVICE_SCREEN',
        'Referral': 'VIEW_REFERRAL_SCREEN',
        'Review Login': 'VIEW_REVIEW_LOGIN_SCREEN'
      };

      const eventKey = screenEventMap[screenName];
      
      if (eventKey && PLATFORM_EVENTS[eventKey]) {
        this.trackPlatformEvent(eventKey, {
          screen_name: screenName,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          page_title: typeof document !== 'undefined' ? document.title : '',
          page_path: typeof window !== 'undefined' ? window.location.pathname : '',
          ...properties
        });
      } else {
        // Fallback to generic screen view
        this.track('screen_view', {
          screen_name: screenName,
          page_url: typeof window !== 'undefined' ? window.location.href : '',
          page_title: typeof document !== 'undefined' ? document.title : '',
          page_path: typeof window !== 'undefined' ? window.location.pathname : '',
          ...properties
        });
      }
    } catch (error) {
      console.error('Failed to track screen view:', error);
    }
  }

  // Funnel tracking methods
  trackFunnel(funnelName, stepName, properties = {}) {
    const funnel = FUNNELS[funnelName];
    if (!funnel) {
      console.warn(`Unknown funnel: ${funnelName}`);
      return;
    }

    const stepIndex = funnel.steps.indexOf(stepName);
    if (stepIndex === -1) {
      console.warn(`Unknown step ${stepName} in funnel ${funnelName}`);
      return;
    }

    this.trackFunnelStep(funnelName, stepName, stepIndex + 1, properties);
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();

// Export both the class and the instance
export { AnalyticsService };
export default analyticsService;
